import React, { useState, useEffect, useMemo } from 'react';
import { Mail, Paperclip, X, Download, Image, Reply, Link, Maximize2, Minimize2 } from 'lucide-react';
import apiService from '../services/apiService';
import TicketCustomerField from './TicketCustomerField';
import { CalenderMarkerHeaderSelect } from '../utils/calenderMarkerUtils';
import { findCustomerIdByEmail } from '../utils/customerCache';
import '../styles/EmailDetail.css';
import { useI18n } from '../utils/i18n';
import Modal from './ui/Modal';
import ExternalHtmlFrame from './ui/ExternalHtmlFrame';
import { sanitizeExternalHtml } from '../utils/sanitizeExternalHtml';
import { ticketPasstZurSuche } from '../utils/suchFilter';
import { useFeature } from '../utils/featuresCache';
import { useToast } from './ToastProvider';
import { useMailAnhangViewer } from './ticketDetail/MailAnhaenge';
import { anhangArt } from '../utils/anhangArt';

// mobil (#634): aus der Mobilansicht geöffnet. Ändert nur, wie der Antwort-
// Entwurf aufgeht (siehe handleReplyToEmail).
const EmailDetail = ({ email, onClose, onEmailUpdate, mobil = false }) => {
  const { getLocale, t } = useI18n();
  const kundenAktiv = useFeature('customers'); // #251: Platzhalter nennt Kunde nur, wenn der Kundenstamm an ist
  const { pushToast } = useToast();
  // #636: Bilder und PDFs der Mail öffnen im Betrachter statt als Download.
  const anhangViewer = useMailAnhangViewer();
  const [attachments, setAttachments] = useState([]);
  const [processedBodyContent, setProcessedBodyContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAttachmentsList, setShowAttachmentsList] = useState(false);
  const [bodyProcessed, setBodyProcessed] = useState(false);
  const [, setIsReadState] = useState(Boolean(email?.isRead));
  const [isMaximized, setIsMaximized] = useState(false);

  // Reply functionality state
  const [replyingEmail, setReplyingEmail] = useState(false);
  const [showReplyTicketDialog, setShowReplyTicketDialog] = useState(false);
  const [replyEmailForTicket, setReplyEmailForTicket] = useState(null);
  const [replyTicketData, setReplyTicketData] = useState({
    title: '',
    description: '',
    priority: 'normal',
    category: 'E-Mail'
  });

  const mailboxAddress = useMemo(() => {
    return email?.mailboxAddress || email?.__mailbox || undefined;
  }, [email]);

  // Add-to-ticket state
  const [isEmailLinked, setIsEmailLinked] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [openTickets, setOpenTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [availableEmails, setAvailableEmails] = useState([]);
  const [ticketFilterText, setTicketFilterText] = useState('');
  const [showCreateTicketForm, setShowCreateTicketForm] = useState(false);
  const [ticketActionLoading, setTicketActionLoading] = useState(false);
  const [createTicketData, setCreateTicketData] = useState({
    title: '',
    description: '',
    priority: 'normal',
    category: 'E-Mail',
    calenderMarker: null,
    assignedTo: '',
    customerId: null,
    contactId: null
  });

  useEffect(() => {
    console.log(`📧 [EMAIL DETAIL] EmailDetail component mounted for email:`, email?.id);
    console.log(`📧 [EMAIL DETAIL] Email object:`, email);
    
    const mailboxAddress = email?.mailboxAddress || email?.__mailbox || undefined;

    // Reset state when email changes
    setBodyProcessed(false);
    setProcessedBodyContent('');
    
    if (email) {
      // Sync initial read state when email changes
      setIsReadState(Boolean(email.isRead));

      const ensureFullEmailLoaded = async () => {
        try {
          const lacksBody = !email.body || (typeof email.body === 'object' && !email.body.content);
          if (lacksBody) {
            console.log('📧 [EMAIL DETAIL] No full body present, loading email details from server...');
            const full = await apiService.getEmailDetails(email.id, mailboxAddress);
            // Merge fields we care about; prefer existing fields if present
            const merged = { ...email, ...full };
            // Update the content processing using merged data without changing parent state shape
            await checkForAttachmentsNeededWithEmail(merged);
            return;
          }
        } catch (loadErr) {
          console.error('📧 [EMAIL DETAIL] Failed to load full email details:', loadErr);
        }
        // Fallback: proceed with current email
        await checkForAttachmentsNeededWithEmail(email);
      };

      ensureFullEmailLoaded();

      // Prüfe Verknüpfungsstatus für Add-to-Ticket Button
      (async () => {
        try {
          const response = await apiService.getLinkedItems();
          const linkedIds = response.linkedEmailIds || [];
          setIsEmailLinked(linkedIds.includes(email.id));
        } catch (err) {
          console.error('📧 [EMAIL DETAIL] Error loading linked status:', err);
          setIsEmailLinked(false);
        }
      })();
    }
  }, [email]);

  // Markiere E-Mail automatisch als gelesen, sobald die Detailansicht geöffnet wird
  useEffect(() => {
    (async () => {
      try {
        if (email && !email.isRead) {
          await apiService.markEmailAsRead(email.id, true, mailboxAddress);
          setIsReadState(true);
          if (onEmailUpdate) {
            onEmailUpdate();
          }
        }
      } catch (err) {
        console.error('📧 [EMAIL DETAIL] Fehler beim automatischen Markieren als gelesen:', err);
      }
    })();
  }, [email]);

  const checkForAttachmentsNeededWithEmail = async (emailObj) => {
    if (!emailObj || !emailObj.body) {
      console.log(`📎 [EMAIL DETAIL] No email or body to check`);
      processEmailBody(undefined, emailObj);
      return;
    }

    // Handle different body formats to extract content
    let bodyContent;
    if (typeof emailObj.body === 'string') {
      bodyContent = emailObj.body;
    } else if (typeof emailObj.body === 'object' && emailObj.body.content) {
      bodyContent = emailObj.body.content;
    } else {
      console.log(`📎 [EMAIL DETAIL] Unexpected body format, proceeding without attachments`);
      processEmailBody(undefined, emailObj);
      return;
    }

    // Check for CID references in the body
    const cidRegex = /cid:([^"'\s>]+)/gi;
    const cidMatches = [...bodyContent.matchAll(cidRegex)];
    const hasCidReferences = cidMatches.length > 0;
    
    console.log(`📎 [EMAIL DETAIL] hasAttachments: ${emailObj.hasAttachments}, CID references found: ${cidMatches.length}`);
    
    if (emailObj.hasAttachments || hasCidReferences) {
      console.log(`📎 [EMAIL DETAIL] Loading attachments (hasAttachments: ${emailObj.hasAttachments}, CID refs: ${cidMatches.length})`);
      loadAttachments(emailObj);
    } else {
      console.log(`📎 [EMAIL DETAIL] No attachments needed, processing body directly`);
      processEmailBody(undefined, emailObj);
    }
  };


  const loadAttachments = async (emailObj = email) => {
    try {
      setLoading(true);
      console.log(`📎 [EMAIL DETAIL] Loading attachments for email ${emailObj.id}`);
      
      const response = await apiService.getEmailAttachments(emailObj.id, mailboxAddress);
      const attachmentList = response.attachments || [];
      
      console.log(`📎 [EMAIL DETAIL] Loaded ${attachmentList.length} attachments:`, attachmentList);
      
      // Debug: Log download URLs to verify they are correctly formatted
      attachmentList.forEach((att, index) => {
        if (att.isInline && att.contentId) {
          console.log(`📸 [EMAIL DETAIL DEBUG] Inline attachment ${index + 1}: ${att.name}`);
          console.log(`📸 [EMAIL DETAIL DEBUG] Content-ID: ${att.contentId}`);
          console.log(`📸 [EMAIL DETAIL DEBUG] Download URL: ${att.downloadUrl}`);
          console.log(`📸 [EMAIL DETAIL DEBUG] Source: ${att.cached ? 'cache' : 'live'}`);
        }
      });
      
      setAttachments(attachmentList);
      
      // Verarbeite den E-Mail-Body mit den Anhängen
      await processEmailBody(attachmentList, emailObj);
      
    } catch (error) {
      console.error('📎 [EMAIL DETAIL] Error loading attachments:', error);
      
      // Wichtig: Auch bei Fehlern den Body verarbeiten für CID-Bereinigung
      console.log('📎 [EMAIL DETAIL] Proceeding with body processing despite attachment error');
      processEmailBody([], emailObj);
    } finally {
      setLoading(false);
    }
  };

  const processEmailBody = async (attachmentList = [], emailObj = email) => {
    console.log(`📸 [EMAIL DETAIL] Processing email body...`);
    
    if (!emailObj || !emailObj.body) {
      console.log(`📸 [EMAIL DETAIL] No email or body found`);
      setProcessedBodyContent('');
      setBodyProcessed(true); // Mark as processed even if empty
      return;
    }

    // Handle different body formats
    let bodyContent;
    if (typeof emailObj.body === 'string') {
      bodyContent = emailObj.body;
    } else if (typeof emailObj.body === 'object' && emailObj.body.content) {
      bodyContent = emailObj.body.content;
    } else {
      console.warn('📸 [EMAIL DETAIL] Unexpected body format:', emailObj.body);
      setProcessedBodyContent(`<div class="email-body-empty">${t('email.body.unknownFormat', 'Unbekanntes E-Mail-Format')}</div>`);
      setBodyProcessed(true); // Mark as processed even with error
      return;
    }

    console.log(`📸 [EMAIL DETAIL] Original body content length: ${bodyContent.length}`);
    console.log(`📸 [EMAIL DETAIL] Body content preview:`, bodyContent.substring(0, 500));

    // #125: Plain-Text-Mails nicht als HTML rendern — dangerouslySetInnerHTML
    // kollabiert sonst alle Zeilenumbrüche zu einem einzigen Textblock.
    // Erkennung über den gespeicherten contentType (falls als Objekt vorhanden),
    // sonst Heuristik: kein einziges HTML-Tag im Inhalt.
    // #273: Zwischengespeicherte Mails liefern body als reine Zeichenkette — der
    // contentType stand dort frueher nirgends, weshalb nur die Heuristik blieb.
    // Jetzt wird er als eigenes Feld mitgefuehrt; live von Graph kommt er
    // weiterhin im body-Objekt. Aeltere Zeilen ohne den Wert fallen wie bisher
    // auf die Heuristik zurueck.
    const declaredType = (typeof emailObj.body === 'object' && emailObj.body.contentType)
      ? String(emailObj.body.contentType).toLowerCase()
      : (emailObj.bodyContentType ? String(emailObj.bodyContentType).toLowerCase() : null);
    const looksLikeHtml = /<([a-z][a-z0-9]*)\b[^>]*>/i.test(bodyContent);
    if (declaredType === 'text' || (!declaredType && !looksLikeHtml)) {
      const escaped = bodyContent
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      setProcessedBodyContent(`<div style="white-space: pre-wrap;">${escaped}</div>`);
      setBodyProcessed(true);
      return;
    }

    // Suche nach CID-Referenzen und ersetze sie durch echte URLs
    let processedContent = bodyContent;
    
    // Erweiterte Regex für verschiedene CID-Formate
    const cidRegex = /cid:([^"'\s>]+)/gi;
    const cidMatches = [...bodyContent.matchAll(cidRegex)];
    
    console.log(`📸 [EMAIL DETAIL] Found ${cidMatches.length} CID references:`, cidMatches.map(m => m[0]));
    
    if (cidMatches.length > 0 && attachmentList.length > 0) {
      console.log(`📸 [EMAIL DETAIL] Processing CID references with ${attachmentList.length} attachments`);
      
      // Log alle verfügbaren Content-IDs
      const availableContentIds = attachmentList
        .filter(att => att.contentId)
        .map(att => att.contentId);
      console.log(`📸 [EMAIL DETAIL] Available Content-IDs:`, availableContentIds);
      
      for (const match of cidMatches) {
        const fullCidMatch = match[0]; // z.B. "cid:image001.png@01DBF251.7DAE9DB0"
        const contentId = match[1]; // z.B. "image001.png@01DBF251.7DAE9DB0"
        
        console.log(`📸 [EMAIL DETAIL] Processing CID: ${fullCidMatch} -> Content-ID: ${contentId}`);
        
        // Finde den passenden Anhang - mehrere Vergleichsmethoden
        let matchingAttachment = attachmentList.find(att => {
          const attContentId = att.contentId;
          if (!attContentId) return false;
          
          // Exakte Übereinstimmung
          if (attContentId === contentId) {
            console.log(`📸 [EMAIL DETAIL] Found exact match for ${contentId}`);
            return true;
          }
          
          // Übereinstimmung ohne Winkel-Klammern
          if (attContentId.replace(/[<>]/g, '') === contentId) {
            console.log(`📸 [EMAIL DETAIL] Found match without angle brackets for ${contentId}`);
            return true;
          }
          
          // Partial Match (falls @ oder : im Content-ID unterschiedlich behandelt werden)
          if (attContentId.includes(contentId.split('@')[0]) || contentId.includes(attContentId.split('@')[0])) {
            console.log(`📸 [EMAIL DETAIL] Found partial match for ${contentId}`);
            return true;
          }
          
          return false;
        });
        
        if (matchingAttachment) {
          console.log(`📸 [EMAIL DETAIL] Replacing CID ${fullCidMatch} with download URL: ${matchingAttachment.downloadUrl}`);
          
          // Ersetze alle Vorkommen der CID-Referenz durch die Download-URL
          const cidPattern = new RegExp(fullCidMatch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
          const beforeReplace = processedContent;
          processedContent = processedContent.replace(cidPattern, matchingAttachment.downloadUrl);
          
          if (beforeReplace !== processedContent) {
            console.log(`📸 [EMAIL DETAIL] ✅ Successfully replaced CID ${fullCidMatch}`);
          } else {
            console.warn(`📸 [EMAIL DETAIL] ❌ Failed to replace CID ${fullCidMatch} - regex might not match`);
          }
        } else {
          console.warn(`📸 [EMAIL DETAIL] No matching attachment found for CID: ${fullCidMatch}`);
          console.log(`📸 [EMAIL DETAIL] Available attachments:`, attachmentList.map(att => ({
            name: att.name,
            contentId: att.contentId,
            isInline: att.isInline
          })));
          
          // Entferne nicht gefundene CID-Referenzen
          const cidPattern = new RegExp(fullCidMatch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
          processedContent = processedContent.replace(cidPattern, t('email.body.imageNotAvailable', '[Bild nicht verfügbar]'));
          console.log(`📸 [EMAIL DETAIL] Replaced missing CID ${fullCidMatch} with placeholder`);
        }
      }
    } else if (cidMatches.length > 0) {
      console.log(`📸 [EMAIL DETAIL] Found CID references but no attachments loaded - removing CID references`);
      
      // Entferne alle CID-Referenzen wenn keine Anhänge vorhanden sind
      for (const match of cidMatches) {
        const fullCidMatch = match[0];
        const cidPattern = new RegExp(fullCidMatch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
        processedContent = processedContent.replace(cidPattern, t('email.body.imageNotAvailable', '[Bild nicht verfügbar]'));
        console.log(`📸 [EMAIL DETAIL] Removed CID reference: ${fullCidMatch}`);
      }
    }

    console.log(`📸 [EMAIL DETAIL] Processed body content length: ${processedContent.length}`);
    console.log(`📸 [EMAIL DETAIL] Body content changed:`, processedContent !== bodyContent);
    
    // Forciere ein Re-Rendering
    console.log(`📸 [EMAIL DETAIL] Setting processed content...`);
    setProcessedBodyContent(sanitizeExternalHtml(processedContent));
    setBodyProcessed(true); // Mark body as processed
    
    // Debug: Log final processed content preview
    console.log(`📸 [EMAIL DETAIL] Final processed content preview:`, processedContent.substring(0, 500));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString(getLocale(), {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getImportanceColor = (importance) => {
    switch (importance) {
      case 'high': return 'var(--color-wichtigkeit-hoch)';
      case 'low': return 'var(--color-wichtigkeit-niedrig)';
      default: return 'var(--color-wichtigkeit-normal)';
    }
  };

  const getImportanceText = (importance) => {
    switch (importance) {
      case 'high': return t('email.importance.high', 'Hoch');
      case 'low': return t('email.importance.low', 'Niedrig');
      default: return t('email.importance.normal', 'Normal');
    }
  };

  const handleReplyToEmail = async (e) => {
    e.stopPropagation(); // Prevent any parent click events

    // #634: Handy-Browser blockieren ein Fenster, das erst nach dem
    // Serveraufruf aufgeht (keine Nutzeraktion mehr). Mobil wird der Tab
    // deshalb sofort im Klick geöffnet und erst danach auf den Entwurf gelenkt.
    let zielFenster = null;
    if (mobil) {
      zielFenster = window.open('', '_blank');
      if (zielFenster) zielFenster.opener = null;
    }

    try {
      setReplyingEmail(true);
      
      // Create the reply draft
      const response = await apiService.createEmailReply(email.id, {});
      
      if (response.success && response.webLink) {
        if (response.sendFromFallback && pushToast) {
          const fallbackMailbox = response.mailboxContext || email?.mailboxAddress || 'Postfach';
          pushToast({
            message: t('email.reply.sendFromFallback', 'Keine Senden-Als Rechte an {{mailbox}}, sende aus eigenem Konto.', { mailbox: fallbackMailbox }),
            timeoutMs: 5000
          });
        }
        // Debug: Show original link
        console.log('🔍 [EMAIL REPLY DEBUG] Original webLink received:', response.webLink);
        
        // Convert /owa/ URL to /mail/deeplink/compose/ URL to open in edit mode
        let editModeLink = response.webLink;
        
        // Check if it's an /owa/ URL and convert it to /mail/deeplink/compose/
        if (response.webLink.includes('/owa/') && response.webLink.includes('ItemID=')) {
          const url = new URL(response.webLink);
          const itemId = url.searchParams.get('ItemID');
          const baseUrl = url.origin;
          
          // Build the compose URL
          editModeLink = `${baseUrl}/mail/deeplink/compose/${encodeURIComponent(itemId)}?ItemID=${encodeURIComponent(itemId)}&exvsurl=1`;
        }
        
        // Debug: Show modified link
        console.log('🔍 [EMAIL REPLY DEBUG] Modified webLink (owa->compose):', editModeLink);
        console.log('🔍 [EMAIL REPLY DEBUG] Original contains /owa/:', response.webLink.includes('/owa/'));
        console.log('🔍 [EMAIL REPLY DEBUG] Modified contains /mail/deeplink/compose/:', editModeLink.includes('/mail/deeplink/compose/'));
        
        if (mobil) {
          if (zielFenster) zielFenster.location.href = editModeLink;
          else pushToast({ message: t('email.reply.popupBlocked', 'Der Entwurf liegt in Outlook bereit, ließ sich hier aber nicht öffnen.'), timeoutMs: 6000 });
        } else {
          // Open the email draft in a new small window
          window.open(
            editModeLink, 
            'outlook_reply', 
            'width=1200,height=800,scrollbars=yes,resizable=yes,toolbar=no,location=yes,directories=no,status=no,menubar=no,left=100,top=100'
          );
        }
        
        // Debug: Confirm what was opened
        console.log('🔍 [EMAIL REPLY DEBUG] Opening URL:', editModeLink);
        
        // Trigger email list refresh to show updated ticket info
        if (onEmailUpdate) {
          console.log('📧 [EMAIL DETAIL] Triggering email list refresh after reply creation');
          onEmailUpdate();
        }
        
        // Success: Email draft created with ticket number in subject
        // No alert needed - user can see the result in Outlook
      } else if (response.requiresTicketDecision) {
        if (zielFenster) zielFenster.close();
        // Email is not linked to a ticket, show dialog to create one
        setReplyEmailForTicket(response.emailInfo);
        setReplyTicketData({
          title: response.emailInfo.subject || 'Neues Ticket',
          description: response.emailInfo.bodyPreview || '',
          priority: 'normal',
          category: 'E-Mail'
        });
        setShowReplyTicketDialog(true);
      } else {
        if (zielFenster) zielFenster.close();
        pushToast({ type: 'error', message: t('email.errors.draftCreate', 'Fehler beim Erstellen des E-Mail-Entwurfs.') });
      }
      
    } catch (error) {
      if (zielFenster) zielFenster.close();
      console.error('Error creating email reply:', error);
      let errorMessage = t('email.reply.createErrorGeneric', 'Fehler beim Erstellen der E-Mail-Antwort.');
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      // Kehrwoche B (#31): vorher Toast + alert() nebeneinander — jetzt EIN Fehlerpfad;
      // der Senden-Als-Hinweis wird an die Fehlermeldung angehängt statt als zweiter Toast.
      if (error.response?.data?.sendFromFallback) {
        const fallbackMailbox = error.response.data.mailboxContext || email?.mailboxAddress || 'Postfach';
        errorMessage += ' ' + t('email.reply.sendFromFallback', 'Keine Senden-Als Rechte an {{mailbox}}, sende aus eigenem Konto.', { mailbox: fallbackMailbox });
      }

      pushToast({ type: 'error', message: errorMessage });
    } finally {
      setReplyingEmail(false);
    }
  };

  const handleCreateReplyTicket = async () => {
    if (!replyEmailForTicket) {
      pushToast({ type: 'error', message: t('email.errors.noneSelected', 'Fehler: Keine E-Mail ausgewählt') });
      return;
    }
    
    setLoading(true);
    try {
      // Create the reply draft with new ticket
      const response = await apiService.createEmailReply(replyEmailForTicket.id, {
        createNewTicket: true,
        newTicketData: replyTicketData
      });
      
      if (response.success && response.webLink) {
        if (response.sendFromFallback && pushToast) {
          const fallbackMailbox = response.mailboxContext || email?.mailboxAddress || 'Postfach';
          pushToast({
            message: t('email.reply.sendFromFallback', 'Keine Senden-Als Rechte an {{mailbox}}, sende aus eigenem Konto.', { mailbox: fallbackMailbox }),
            timeoutMs: 5000
          });
        }
        // Close dialog
        setShowReplyTicketDialog(false);
        setReplyEmailForTicket(null);
        
        // Debug: Show original link  
        console.log('🔍 [CREATE REPLY TICKET DEBUG] Original webLink received:', response.webLink);
        
        // Convert /owa/ URL to /mail/deeplink/compose/ URL to open in edit mode
        let editModeLink = response.webLink;
        
        // Check if it's an /owa/ URL and convert it to /mail/deeplink/compose/
        if (response.webLink.includes('/owa/') && response.webLink.includes('ItemID=')) {
          const url = new URL(response.webLink);
          const itemId = url.searchParams.get('ItemID');
          const baseUrl = url.origin;
          
          // Build the compose URL
          editModeLink = `${baseUrl}/mail/deeplink/compose/${encodeURIComponent(itemId)}?ItemID=${encodeURIComponent(itemId)}&exvsurl=1`;
        }
        
        // Debug: Show modified link
        console.log('🔍 [CREATE REPLY TICKET DEBUG] Modified webLink (owa->compose):', editModeLink);
        console.log('🔍 [CREATE REPLY TICKET DEBUG] Original contains /owa/:', response.webLink.includes('/owa/'));
        console.log('🔍 [CREATE REPLY TICKET DEBUG] Modified contains /mail/deeplink/compose/:', editModeLink.includes('/mail/deeplink/compose/'));
        
        // Open the email draft in a new small window
        window.open(
          editModeLink, 
          'outlook_reply', 
          'width=1200,height=800,scrollbars=yes,resizable=yes,toolbar=no,location=yes,directories=no,status=no,menubar=no,left=100,top=100'
        );
        
        // Debug: Confirm what was opened
        console.log('🔍 [CREATE REPLY TICKET DEBUG] Opening URL:', editModeLink);
        
        // Trigger email list refresh to show that email is now linked to ticket
        if (onEmailUpdate) {
          console.log('📧 [EMAIL DETAIL] Triggering email list refresh after ticket creation');
          onEmailUpdate();
        }
        // Dashboard-Ticketlisten sofort aktualisieren
        try { window.dispatchEvent(new Event('dashboardEventRefresh')); } catch (_) {}
        
        // Success: Email draft created and new ticket created
        // No alert needed - user can see the result in Outlook
      } else {
        pushToast({ type: 'error', message: t('email.errors.draftCreate', 'Fehler beim Erstellen des E-Mail-Entwurfs.') });
      }
      
    } catch (error) {
      console.error('Error creating reply with ticket:', error);
      pushToast({ type: 'error', message: t('email.errors.replyCreate', 'Fehler beim Erstellen der E-Mail-Antwort: {{details}}', { details: error.message }) });
    } finally {
      setLoading(false);
    }
  };

  const handleReplyWithoutTicket = async () => {
    if (!replyEmailForTicket) {
      pushToast({ type: 'error', message: t('email.errors.noneSelected', 'Fehler: Keine E-Mail ausgewählt') });
      return;
    }
    
    setLoading(true);
    try {
      // Create the reply draft without ticket
      const response = await apiService.createEmailReply(replyEmailForTicket.id, {
        createNewTicket: false,
        newTicketData: {}
      });
      
      if (response.success && response.webLink) {
        // Close dialog
        setShowReplyTicketDialog(false);
        setReplyEmailForTicket(null);
        
        // Open the email draft in a new small window
        window.open(
          response.webLink, 
          'outlook_reply', 
          'width=1200,height=800,scrollbars=yes,resizable=yes,toolbar=no,location=yes,directories=no,status=no,menubar=no,left=100,top=100'
        );
      } else {
        pushToast({ type: 'error', message: t('email.errors.draftCreate', 'Fehler beim Erstellen des E-Mail-Entwurfs.') });
      }
      
    } catch (error) {
      console.error('Error creating reply without ticket:', error);
      pushToast({ type: 'error', message: t('email.errors.replyCreate', 'Fehler beim Erstellen der E-Mail-Antwort: {{details}}', { details: error.message }) });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelReplyTicket = () => {
    setShowReplyTicketDialog(false);
    setReplyEmailForTicket(null);
    setReplyTicketData({
      title: '',
      description: '',
      priority: 'normal',
      category: 'E-Mail'
    });
  };

  // ================ ADD TO TICKET HANDLERS ================

  const loadOpenTickets = async () => {
    setLoadingTickets(true);
    try {
      const [ticketsResponse, emailsResponse] = await Promise.all([
        apiService.getOpenTickets(),
        apiService.getAvailableEmails()
      ]);
      setOpenTickets(ticketsResponse.tickets || []);
      setAvailableEmails(emailsResponse.emails || []);
    } catch (error) {
      console.error('📧 [EMAIL DETAIL] Error loading open tickets:', error);
      setOpenTickets([]);
      setAvailableEmails([]);
    } finally {
      setLoadingTickets(false);
    }
  };

  const handleOpenAddToTicket = () => {
    setTicketFilterText('');
    setShowCreateTicketForm(false);
    setShowTicketModal(true);
    loadOpenTickets();
  };

  // #251: Nummer, Titel, Kunde, Ansprechpartner -- gemeinsamer Helfer
  const filteredTickets = openTickets.filter(ticket => ticketPasstZurSuche(ticket, ticketFilterText));

  const handleAddEmailToTicket = async (ticketId) => {
    if (!email) {
      pushToast({ type: 'error', message: t('email.errors.noneSelected', 'Fehler: Keine E-Mail ausgewählt') });
      return;
    }
    setTicketActionLoading(true);
    try {
      await apiService.addEmailToTicket(ticketId, email.id, null, email);
      setShowTicketModal(false);
      setIsEmailLinked(true);
      if (onEmailUpdate) {
        await onEmailUpdate();
      }
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('📧 [EMAIL DETAIL] Error adding email to ticket:', error);
      pushToast({ type: 'error', message: t('email.errors.addToTicket', 'Fehler beim Hinzufügen der E-Mail zum Ticket: {{details}}', { details: error.message }) });
    } finally {
      setTicketActionLoading(false);
    }
  };

  const handleCreateNewTicket = () => {
    if (email) {
      setCreateTicketData({
        title: email.subject || 'Neues Ticket',
        description: email.bodyPreview || '',
        priority: 'normal',
        category: 'E-Mail',
        calenderMarker: null,
        assignedTo: '',
        customerId: null,
        contactId: null
      });
      // #210: per Absender-Domäne erkannten Kunden vorbelegen — sichtbar und
      // überschreibbar (nur setzen, solange der Benutzer nichts gewählt hat)
      const sender = email.from?.emailAddress?.address || email.fromAddress || null;
      findCustomerIdByEmail(sender).then(cid => {
        if (cid) setCreateTicketData(prev => (prev.customerId ? prev : { ...prev, customerId: cid }));
      });
    }
    setShowCreateTicketForm(true);
  };

  const handleSubmitNewTicket = async (e) => {
    e.preventDefault();
    if (!email) {
      pushToast({ type: 'error', message: t('email.errors.noneSelected', 'Fehler: Keine E-Mail ausgewählt') });
      return;
    }
    setTicketActionLoading(true);
    try {
      await apiService.createTicketFromEmail(email.id, createTicketData, email);
      setShowTicketModal(false);
      setShowCreateTicketForm(false);
      setIsEmailLinked(true);
      if (onEmailUpdate) {
        await onEmailUpdate();
      }
      // Dashboard-Ticketlisten sofort aktualisieren
      try { window.dispatchEvent(new Event('dashboardEventRefresh')); } catch (_) {}
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('📧 [EMAIL DETAIL] Error creating ticket from email:', error);
      pushToast({ type: 'error', message: t('ticket.createErrorDetails', 'Fehler beim Erstellen des Tickets: {{details}}', { details: error.message }) });
    } finally {
      setTicketActionLoading(false);
    }
  };

  const handleCancelCreateTicket = () => {
    setShowCreateTicketForm(false);
    setCreateTicketData({
      title: '',
      description: '',
      priority: 'normal',
      category: 'E-Mail',
      calenderMarker: null,
      assignedTo: '',
      customerId: null,
      contactId: null
    });
  };

  const parseRecipients = (recipients) => {
    if (!recipients) return [];
    if (typeof recipients === 'string') {
      try {
        return JSON.parse(recipients);
      } catch {
        return recipients.split(',').map(r => ({ emailAddress: { address: r.trim() } }));
      }
    }
    return recipients;
  };

  const renderRecipientList = (recipients) => {
    const recipientList = parseRecipients(recipients);
    return recipientList.map((recipient, index) => (
      <span key={index} className="recipient">
        {recipient.emailAddress?.name || recipient.emailAddress?.address || recipient}
        {index < recipientList.length - 1 && ', '}
      </span>
    ));
  };

  const renderEmailBody = () => {
    if (!email) {
      return <div className="email-body-empty">{t('email.body.empty', 'Kein Inhalt verfügbar')}</div>;
    }

    if (!bodyProcessed) {
      return (
        <div className="email-body-empty">
          <div className="loading-indicator">{t('email.body.processing', 'Inhalt wird verarbeitet...')}</div>
        </div>
      );
    }

    if (!processedBodyContent) {
      return <div className="email-body-empty">{t('email.body.empty', 'Kein Inhalt verfügbar')}</div>;
    }

    // #273: Der Inhalt laeuft in einem abgeschotteten Rahmen (eigenes Dokument,
    // keine Skripte, kein Zugriff auf die Anwendung) — dadurch behaelt die Mail
    // ihr eigenes CSS und die Anwendung bleibt davon unberuehrt.
    //
    // Entfallen ist dabei der frueher hier haengende onLoad/onError-Block, der
    // fehlgeschlagene Bilder durch einen Platzhalter ersetzen sollte: Ein <div>
    // kennt weder ein load- noch ein error-Ereignis, der Code lief also nie.
    // Der Rahmen sitzt IM bestehenden Kasten, nicht an seiner Stelle:
    // .email-body-content traegt max-height + overflow-y:auto. Auf einem
    // <iframe> wuerde das nicht funktionieren — ein ersetztes Element bekommt
    // durch overflow keine Bildlaufleiste, lange Mails waeren bei 400 px
    // abgeschnitten und der Rest unerreichbar. Also scrollt weiterhin der
    // Kasten, und der Rahmen darin ist so hoch wie sein Inhalt.
    return (
      <div className="email-body-content">
        <ExternalHtmlFrame
          html={processedBodyContent}
          title={t('email.body.frameTitle', 'E-Mail-Inhalt')}
        />
      </div>
    );
  };

  const renderAttachmentsList = () => {
    if (!attachments.length) return null;

    // Separiere inline und normale Anhänge
    const inlineAttachments = attachments.filter(att => att.isInline);
    const regularAttachments = attachments.filter(att => !att.isInline);
    const viewerListe = regularAttachments.map(a => ({ id: a.id, emailId: email.id, name: a.name, contentType: a.contentType, size: a.size }));

    return (
      <div className="email-attachments-section">
        <div className="attachments-header">
          <h4>
            <Paperclip size={16} style={{ marginRight: '8px' }} />
            {t('email.attachments.title', 'Anhänge')} ({attachments.length})
          </h4>
          <button 
            className="toggle-attachments-btn"
            onClick={() => setShowAttachmentsList(!showAttachmentsList)}
          >
            {showAttachmentsList ? t('common.hide', 'Ausblenden') : t('common.show', 'Anzeigen')}
          </button>
        </div>

        {showAttachmentsList && (
          <div className="attachments-list">
            {regularAttachments.map((attachment, index) => (
              <div key={index} className="attachment-item">
                <div className="attachment-info">
                  {anhangArt(attachment) ? (
                    <button
                      type="button"
                      className="attachment-name attachment-ansehen"
                      onClick={() => anhangViewer.oeffnenFuer(viewerListe)({ id: attachment.id, emailId: email.id })}
                    >
                      {attachment.name}
                    </button>
                  ) : (
                    <span className="attachment-name">{attachment.name}</span>
                  )}
                  <span className="attachment-size">
                    {attachment.size ? `${(attachment.size / 1024).toFixed(1)} KB` : t('email.attachments.unknownSize', 'Unbekannte Größe')}
                  </span>
                </div>
                <a 
                  href={attachment.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="attachment-download-btn"
                >
                  <Download size={16} />
                </a>
              </div>
            ))}
            
            {inlineAttachments.length > 0 && (
              <div className="inline-attachments-info">
                <Image size={16} style={{ marginRight: '8px' }} />
                <span>{inlineAttachments.length} {t('email.attachments.inlineImages', 'eingebettete Bilder')}</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (!email) return null;

  return (
    <div className="email-detail-overlay">
      <div className={`email-detail-container${isMaximized ? ' maximized' : ''}`}>
        <div className="email-detail-header">
          <div className="email-header-info">
            <Mail size={20} />
            <h2>{t('email.detail.title', 'E-Mail Details')}</h2>
          </div>
          <button className="close-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="email-detail-content">
          {!isMaximized && (
            <>
              <div className="email-meta-section">
                <div className="email-meta-grid">
                  <div className="meta-row">
                    <div className="meta-item">
                      <span className="meta-label">{t('email.detail.from', 'Von:')}</span>
                      <span className="meta-value">
                        {email.fromName || email.fromAddress || email.from?.emailAddress?.name || email.from?.emailAddress?.address || t('common.unknown', 'Unbekannt')}
                      </span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">{t('email.detail.to', 'An:')}</span>
                      <span className="meta-value">
                        {email.toAddresses ? renderRecipientList(email.toAddresses) :
                         email.toRecipients ? renderRecipientList(email.toRecipients) : t('common.notAvailable', 'Nicht verfügbar')}
                      </span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">{t('email.detail.received', 'Empfangen:')}</span>
                      <span className="meta-value">
                        {formatDate(email.receivedDateTime)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="email-subject">
                  <h3>
                    {email.subject || t('email.subject.none', '(Kein Betreff)')}
                    {email.hasAttachments && (
                      <Paperclip size={16} className="attachment-icon" />
                    )}
                  </h3>
                  <div className="email-importance" style={{ color: getImportanceColor(email.importance) }}>
                    {getImportanceText(email.importance)}
                  </div>
                </div>

                <div className="email-flags">
                  {email.isRead ? (
                    <span className="flag-badge read">✓ {t('email.read', 'Gelesen')}</span>
                  ) : (
                    <span className="flag-badge unread">● {t('email.unread', 'Ungelesen')}</span>
                  )}
                  {email.flag?.flagStatus === 'flagged' && (
                    <span className="flag-badge flagged">🚩 {t('email.flagged', 'Markiert')}</span>
                  )}
                </div>
              </div>

              {/* Anhänge-Bereich */}
              {email.hasAttachments && renderAttachmentsList()}
            </>
          )}

          <div className="email-body-section">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: isMaximized ? 'flex-end' : 'space-between', marginBottom: '10px' }}>
              {!isMaximized && (
                <h4 style={{ margin: 0 }}>
                  {t('email.detail.content', 'Inhalt')}
                  {loading && <span className="loading-indicator">{t('email.detail.imagesLoading', ' (Bilder werden geladen...)')}</span>}
                </h4>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {!isEmailLinked && !isMaximized && (
                  <button
                    className="email-add-to-ticket-button"
                    onClick={handleOpenAddToTicket}
                    disabled={ticketActionLoading}
                    title={t('email.addToTicket.title', 'E-Mail zu Ticket hinzufügen')}
                    style={{
                      background: 'linear-gradient(135deg, var(--color-neutral) 0%, var(--color-neutral-dark) 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      cursor: ticketActionLoading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'all 0.2s',
                      opacity: ticketActionLoading ? 0.6 : 1,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Link size={16} />
                    {ticketActionLoading ? t('common.opening', 'Öffne…') : t('email.addToTicket.button', 'Zu einem Ticket hinzufügen')}
                  </button>
                )}
                <button
                  className="email-reply-button"
                  onClick={handleReplyToEmail}
                  disabled={replyingEmail}
                  title={t('email.reply.title', 'Auf E-Mail antworten')}
                  style={{
                    background: 'linear-gradient(135deg, var(--color-primary-legacy) 0%, var(--color-primary-legacy-dark) 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    cursor: replyingEmail ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s',
                    opacity: replyingEmail ? 0.6 : 1,
                    minWidth: '100px',
                    justifyContent: 'center'
                  }}
                >
                  <Reply size={16} />
                  {replyingEmail ? t('common.creating', 'Erstelle...') : t('email.reply.button', 'Antworten')}
                </button>
                <button
                  className="email-maximize-button"
                  onClick={() => setIsMaximized(!isMaximized)}
                  title={isMaximized ? t('email.minimize.title', 'Minimieren') : t('email.maximize.title', 'Maximieren')}
                  style={{
                    background: 'linear-gradient(135deg, var(--color-neutral) 0%, var(--color-neutral-dark) 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s',
                    minWidth: '40px',
                    justifyContent: 'center'
                  }}
                >
                  {isMaximized ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </button>
              </div>
            </div>
            {renderEmailBody()}
          </div>
        </div>
      </div>
      {anhangViewer.viewer}
      {/* ADD TO TICKET MODAL */}
      <Modal
        open={showTicketModal}
        onClose={() => { setShowTicketModal(false); }}
        title={<><Link size={24} /><h3>{t('email.addToTicket.modalTitle', 'E-Mail zu Ticket hinzufügen')}</h3>{showCreateTicketForm && (<CalenderMarkerHeaderSelect value={createTicketData.calenderMarker} onChange={(value) => setCreateTicketData({ ...createTicketData, calenderMarker: value })} />)}</>}
        ariaLabel={t('email.addToTicket.modalTitle', 'E-Mail zu Ticket hinzufügen')}
        maxWidth="600px"
        footer={<button type="button" onClick={() => { setShowTicketModal(false); }} className="btn btn-secondary">{t('common.cancel', 'Abbrechen')}</button>}
      >

            {showCreateTicketForm ? (
              <div className="create-ticket-form">
                <h4>{t('ticket.create.new', 'Neues Ticket erstellen:')}</h4>
                <form onSubmit={handleSubmitNewTicket}>
                  {/* #210: Kunde/Ansprechpartner wie in der Ticketmaske (#104);
                      per Domäne erkannte Zuordnung ist vorbelegt und überschreibbar.
                      Funktionales setState: Kundenwechsel feuert zwei Resets (#105). */}
                  <TicketCustomerField
                    value={createTicketData.customerId}
                    onChange={(customerId) => setCreateTicketData(prev => ({ ...prev, customerId }))}
                    contactValue={createTicketData.contactId}
                    onContactChange={(contactId) => setCreateTicketData(prev => ({ ...prev, contactId }))}
                    inputId="email-detail-ticket-customer"
                    contactInputId="email-detail-ticket-contact"
                  />
                  <div className="form-group">
                    <label>{t('form.title', 'Titel')}</label>
                    <input
                      type="text"
                      value={createTicketData.title}
                      onChange={(e) => setCreateTicketData({ ...createTicketData, title: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('form.description', 'Beschreibung')}</label>
                    <textarea
                      value={createTicketData.description}
                      onChange={(e) => setCreateTicketData({ ...createTicketData, description: e.target.value })}
                      rows="4"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('form.priority', 'Priorität')}</label>
                      <select
                        value={createTicketData.priority}
                        onChange={(e) => setCreateTicketData({ ...createTicketData, priority: e.target.value })}
                      >
                        <option value="niedrig">{t('priority.low', 'Niedrig')}</option>
                        <option value="normal">{t('priority.normal', 'Normal')}</option>
                        <option value="hoch">{t('priority.high', 'Hoch')}</option>
                        <option value="kritisch">{t('priority.critical', 'Kritisch')}</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>{t('form.assignedTo', 'Zugewiesen an')}</label>
                      <select
                        value={createTicketData.assignedTo}
                        onChange={(e) => setCreateTicketData({ ...createTicketData, assignedTo: e.target.value })}
                      >
                        <option value="">{t('common.unassigned', 'Nicht zugewiesen')}</option>
                        {availableEmails.map(addr => (
                          <option key={addr} value={addr}>{addr}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  

                  <div className="form-actions">
                    <button type="button" onClick={handleCancelCreateTicket} className="btn btn-secondary">
                      {t('common.back', 'Zurück')}
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={ticketActionLoading}>
                      {ticketActionLoading ? t('common.creating', 'Erstelle...') : t('ticket.create.submit', 'Ticket erstellen')}
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="ticket-selector">
                <h4>{t('ticket.addToExisting', 'Zu vorhandenem Ticket hinzufügen:')}</h4>

                <div className="ticket-filter">
                  <input
                    type="text"
                    placeholder={(kundenAktiv ? t('ticket.filter.placeholderCustomer', 'Nach Ticketnummer, Titel, Kunde oder Ansprechpartner suchen...') : t('ticket.filter.placeholder', 'Nach Ticketnummer oder Titel suchen...'))}
                    value={ticketFilterText}
                    onChange={(e) => setTicketFilterText(e.target.value)}
                    className="ticket-filter-input"
                  />
                </div>

                {loadingTickets ? (
                  <p>{t('ticket.loading', 'Lade Tickets...')}</p>
                ) : openTickets.length === 0 ? (
                  <p>{t('ticket.noneOpen', 'Keine offenen Tickets gefunden.')}</p>
                ) : filteredTickets.length === 0 ? (
                  <p>{t('ticket.noneMatchFilter', 'Keine Tickets gefunden, die dem Filter entsprechen.')}</p>
                ) : (
                  <div className="ticket-list-scrollable">
                    {filteredTickets.map(ticket => (
                      <div key={ticket.id} className="ticket-item-compact">
                        <div className="ticket-info-compact">
                          <div className="ticket-header-compact">
                            <span className="ticket-number-compact">#{ticket.ticketNumber}</span>
                            <div className="ticket-badges-compact">
                              <span className={`status-badge-compact status-${ticket.status}`}>
                                {ticket.status}
                              </span>
                              <span className={`priority-badge-compact priority-${ticket.priority}`}>
                                {ticket.priority}
                              </span>
                            </div>
                          </div>
                          <div className="ticket-title-compact">{ticket.title}</div>
                        </div>
                        <button 
                          onClick={() => handleAddEmailToTicket(ticket.id)}
                          className="btn btn-secondary btn-compact"
                          disabled={ticketActionLoading}
                        >
                          {ticketActionLoading ? t('common.adding', 'Füge hinzu...') : t('common.add', 'Hinzufügen')}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="create-ticket-option">
                  <button onClick={handleCreateNewTicket} className="btn btn-primary">
                    {t('ticket.create.button', 'Neues Ticket erstellen')}
                  </button>
                </div>
              </div>
            )}

      </Modal>
      
      {/* REPLY TICKET DIALOG */}
      <Modal
        open={showReplyTicketDialog}
        onClose={handleCancelReplyTicket}
        title={t('email.reply.createTitle', 'E-Mail-Antwort erstellen')}
        maxWidth="600px"
        closeDisabled={loading}
        footer={<>
          <button type="button" onClick={handleCancelReplyTicket} className="btn btn-secondary" disabled={loading}>{t('common.cancel', 'Abbrechen')}</button>
          <button type="button" onClick={handleReplyWithoutTicket} className="btn btn-outline-primary" disabled={loading}>{loading ? t('common.creating', 'Erstelle...') : t('email.reply.withoutTicket', 'Ohne Ticket antworten')}</button>
          <button type="button" onClick={handleCreateReplyTicket} className="btn btn-primary" disabled={loading}>{loading ? t('common.creating', 'Erstelle...') : t('email.reply.createAndRespond', 'Ticket erstellen & antworten')}</button>
        </>}
      >

            <div className="reply-ticket-dialog">
              <p style={{ marginBottom: '20px' }}>
                {t('email.reply.ticketDecision', 'Diese E-Mail ist noch nicht mit einem Ticket verknüpft. Möchten Sie ein neues Ticket erstellen oder ohne Ticket antworten?')}
              </p>
              
              <div className="create-ticket-form">
                <h4>{t('ticket.create.data', 'Neue Ticket-Daten:')}</h4>
                <div className="form-group">
                  <label>{t('form.title', 'Titel')}</label>
                  <input
                    type="text"
                    value={replyTicketData.title}
                    onChange={(e) => setReplyTicketData({...replyTicketData, title: e.target.value})}
                  />
                </div>
                
                <div className="form-group">
                  <label>{t('form.description', 'Beschreibung')}</label>
                  <textarea
                    value={replyTicketData.description}
                    onChange={(e) => setReplyTicketData({...replyTicketData, description: e.target.value})}
                    rows="3"
                  />
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('form.priority', 'Priorität')}</label>
                    <select
                      value={replyTicketData.priority}
                      onChange={(e) => setReplyTicketData({...replyTicketData, priority: e.target.value})}
                    >
                      <option value="niedrig">{t('priority.low', 'Niedrig')}</option>
                      <option value="normal">{t('priority.normal', 'Normal')}</option>
                      <option value="hoch">{t('priority.high', 'Hoch')}</option>
                      <option value="kritisch">{t('priority.critical', 'Kritisch')}</option>
                    </select>
                  </div>
                  
                  
                </div>
              </div>
              
        </div>
      </Modal>
    </div>
  );
};

export default EmailDetail; 